import React, { forwardRef } from 'react'
import normalizeOptions from 'src/utils/normalizeOptions'

const addonMarkupInner = addon =>
  addon && typeof addon == 'string'
  ? <span className="input-group-text">{addon}</span>
  : addon

const addonMarkup = (type, addon) => addon &&
  <div className={`input-group-addon input-group-${type}`}>
    {addonMarkupInner(addon)}
  </div>

const InputField = ({
  label, afterLabel, prepend, append, hint, horizontal, bs5,
  id, errors, error = errors?.[id]?.[0],
  required, requiredLabel = required,
  type = 'text',
  // Opt in to the customizable <select> (see sources/bootstrap5/form-select-custom).
  // Deliberately opt-in rather than the default for every select: it needs
  // <button>/<selectedcontent> children, which aren't valid <select> content,
  // and three donate_v6 selects render through here on a Safari 14 floor.
  // Sets the class and the trigger together — they're useless apart.
  customSelect,
  columns = [ 'col-sm-3', 'col-sm-9' ],
  wrapperClass = horizontal ? 'form-group mb-3 row' : 'form-group mb-3',
  labelClass = horizontal ? 'control-label form-label col-form-label' : 'form-label',
  groupClass = 'input-group',
  className = (() => {
    switch (type) {
    case 'select': return [
      'custom-select form-select', customSelect && 'form-select-custom',
    ].filter(Boolean).join(' ')
    case 'color': return 'form-control form-control-color'
    case 'range': return 'form-range'
    default: return 'form-control'
    }
  })(),
  children, options,
  ...props
}, ref) => {

  const labelId = label && `${id}-label`
  const labelMarkup = label &&
    <label id={labelId} htmlFor={id} className={labelClass}>
      {requiredLabel ? <>{label} <abbr title="required">*</abbr></> : label}
    </label>

  const errorId = error && `${id}-error`
  const errorMarkup = error &&
    <div id={errorId} className="help-block invalid-feedback" role="alert">
      <span className="sr-only visually-hidden">{label || id}</span>
      {' '}{error}
    </div>

  const hintId = hint && `${id}-hint`
  const hintMarkup = hint &&
    <small id={hintId} className="help-block d-block form-text text-muted">{hint}</small>

  const inputMarkup = children || (() => {
    const inputProps = {
      ...props, id, ref, required,
      className: `${className} ${error ? 'is-invalid' : ''}`,
      'aria-labelledby': labelId,
      'aria-describedby': [ errorId, hintId ].filter(Boolean).join(' '),
      'aria-invalid': error ? 'true' : null,
      'aria-required': required ? 'true' : null,
    }

    switch (typeof type) {
      case 'object':
        if (!type) break
        // Fall through if object is not null
      case 'function':
        const Input = type
        return <Input {...inputProps} options={options} />
    }

    switch (type) {
      case 'select':
        return (
          <select {...inputProps}>
            {
              // The customizable-select trigger. <selectedcontent> mirrors the
              // chosen option's markup into the closed control, which is what
              // lets a swatch show there and not just in the open picker.
              //
              // React logs three dev-mode warnings for this pattern —
              // "<selectedcontent> is unrecognized", "<button> cannot appear as
              // a child of <select>", and (from rich option labels) "<span>
              // cannot appear as a child of <option>". All three are __DEV__-only
              // in React and absent from react-bundle.production.js, BUT note
              // react_helper.rb:6 hands staff the development bundle even in
              // production, so staff do see them in the console. They're noise,
              // not breakage: the markup is what the HTML spec now prescribes
              // and React's nesting table simply predates it.
              //
              // Don't "fix" it by prepending these imperatively in an effect —
              // React doesn't own nodes it didn't render, and inserting siblings
              // ahead of its children invites reconciliation bugs on update, in
              // a component 17 call sites depend on. If the warnings ever have to
              // go, drop the <button> instead: the UA then renders a default
              // trigger showing the selected option's text, costing only the
              // swatch in the closed control, not the styled picker.
              // type="button" states the intent: a supporting UA owns this
              // button's activation behaviour, but without it the default is
              // submit.
              customSelect &&
              <button type="button"><selectedcontent></selectedcontent></button>
            }

            {normalizeOptions(options)?.map(({ label, ...option }, i) =>
              <option
                key={option.value?.toString() || 'null_option'}
                {...option}
              >{label}</option>
            )}
          </select>
        )
      case 'textarea':
        return <textarea {...inputProps}></textarea>
      default:
        return <input type={type} {...inputProps} />
    }
  })()

  const inputGroupMarkup = (prepend || append) &&
    <div className={`${groupClass} ${error ? 'is-invalid' : ''}`}>
      {bs5 ? addonMarkupInner(prepend) : addonMarkup('prepend', prepend)}
      {inputMarkup}
      {bs5 ? addonMarkupInner(append) : addonMarkup('append', append)}
    </div>

  const horizontalLabelMarkup = horizontal &&
    <div className={columns[0]}>{labelMarkup} {afterLabel}</div>

  const horizontalMarkup = horizontal &&
    <div className={columns[1]}>
      {inputGroupMarkup || inputMarkup}
      {errorMarkup}
      {hintMarkup}
    </div>

  return (
    <div className={`${wrapperClass} row-${id} ${error ? 'has-error' : ''}`}>
      {horizontalLabelMarkup || labelMarkup}
      {horizontal ? null : <> {afterLabel}</>}
      {horizontalMarkup || inputGroupMarkup || inputMarkup}
      {horizontal ? null : errorMarkup}
      {horizontal ? null : hintMarkup}
    </div>
  )
}

export default forwardRef(InputField)
